'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { MeetingItem } from '../type';
import ButtonPaging from '../component/ButtonPaging';
import { getCMSIntlVal, tCMSLineBreak } from '../utils/common';
import { useMedia } from '@ssv-website/hooks';
export interface AgendaCardProps {
  item: MeetingItem;
  /** 自定义类名，方便外层控制布局 */
  className?: string;
}

const AgendaCard: React.FC<AgendaCardProps> = ({ item, className = '' }) => {
  const locale = useLocale();
  const isEN = locale === 'en';
  // 是否采用垂直布局（姓名在上，职务在下）
  const isVerticalLayout = isEN;
  const { timeText } = item || {};
  const title = tCMSLineBreak(getCMSIntlVal(item, 'title', locale));
  const speaker = item.speaker.map((v) => ({
    ...v,
    name: getCMSIntlVal(v, 'name', locale),
    title: getCMSIntlVal(v, 'title', locale),
  }));
  const t = useTranslations('s2025');
  const showSpeaker = speaker && speaker?.some((item) => item.name);

  return (
    <div
      className={`p-6 pr-4 border border-white bg-[rgba(255,255,255,0.40)] overflow-hidden ${className}`}
    >
      <div
        className={`flex flex-col gap-8 relative w-full text-[#121C59] mobile:gap-[18px] h-full overflow-y-auto scrollbarStyle2 scroll-smooth pr-2 `}
      >
        {/* 标题 + 时间 */}
        <div
          className={`flex items-center w-full justify-between gap-3 relative flex-shrink-0`}
        >
          <div className="flex flex-col relative">
            <p className="text-[24px] leading-[1.5] font-400 mobile:text-base">
              {title}
            </p>
            <div
              className={`absolute bottom-0 left-0 h-2 mobile:h-1  ${
                isEN ? 'w-[105px] mobile:w-[52.5px]' : 'w-16 mobile:w-8'
              }`}
              style={{
                background:
                  'linear-gradient(90deg, rgba(28, 63, 240, 0.70) 0.01%, rgba(28, 63, 240, 0.21) 71.34%, rgba(21, 34, 99, 0.00) 99.99%)',
              }}
            ></div>
          </div>
          <div className="leading-[1.5] font-tcn1 h-12 flex items-center justify-center shrink-0 mobile:h-6">
            <p className="text-base leading-[1.5] font-tcn1 mobile:text-xs">
              {timeText}
            </p>
          </div>
        </div>

        {/* 分享人 */}
        <div className="flex flex-col gap-3 mobile:gap-2 w-full min-h-0 flex-1">
          {showSpeaker && (
            <div>
              <p className="leading-[1.5] opacity-65 font-300 text-[12px] mobile:text-[9px] flex-shrink-0">
                {t('speaker')}
              </p>
            </div>
          )}

          <div className="flex flex-col w-full flex-1 min-h-0 gap-2 mobile:gap-3">
            {speaker.map((item, index) => {
              return (
                <div
                  key={index}
                  className={`flex w-full text-[16px] mobile:text-xs ${
                    isVerticalLayout
                      ? 'flex-col gap-2 mobile:gap-1'
                      : 'gap-4 items-start mobile:gap-2'
                  }`}
                >
                  <p
                    className={`shrink-0 leading-[1.5] break-words ${
                      isVerticalLayout
                        ? 'font-400 capitalize'
                        : 'font-300 w-[100px] mobile:w-[50px]'
                    }`}
                  >
                    {item.name}
                  </p>
                  <div
                    className={`flex flex-col gap-1 ${
                      isVerticalLayout ? 'w-full' : 'flex-1 min-w-0'
                    }`}
                  >
                    {item.title && (
                      <p className="leading-[1.5] opacity-60 font-300 break-words">
                        {item.title}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 内阴影高光 */}
        <div
          className={`pointer-events-none absolute inset-0 shadow-[inset_0px_0px_34px_0px_rgba(255,255,255,0.25)] mobile:shadow-[inset_0px_0px_17px_0px_rgba(255,255,255,0.25)]`}
        />
      </div>
    </div>
  );
};

const AgendaCardGrid: React.FC<{ list: MeetingItem[] }> = ({ list }) => {
  const [page, setPage] = useState(1);
  const [isFading, setIsFading] = useState(false);
  const isMobile = useMedia('mobile');

  useEffect(() => {
    if (isMobile) {
      setPage(1);
    }
  }, [isMobile]);

  const pageSize = isMobile ? list.length : 4;

  const total = useMemo(() => {
    return Math.ceil(list.length / pageSize);
  }, [list.length, pageSize]);

  const renderList = useMemo(() => {
    return list.slice((page - 1) * pageSize, page * pageSize);
  }, [list, page, pageSize]);

  const handlePageChange = (newPage: number) => {
    setIsFading(true);
    setTimeout(() => {
      setPage(newPage);
      setTimeout(() => {
        setIsFading(false);
      }, 50);
    }, 300);
  };
  return (
    <>
      <div
        className={`w-fit mt-[32px] min-h-[632px] mobile:min-h-0 mobile:mr-5 mobile:mt-[24px] grid grid-cols-2 gap-3 mobile:gap-1 mobile:grid-cols-none mobile:grid-rows-1 mobile:grid-flow-col mobile:auto-cols-max transition-opacity duration-300 ${
          isFading ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {renderList.map((item) => (
          <AgendaCard
            key={item.id}
            item={item}
            className="w-[680px] h-[310px] mobile:w-[324px] mobile:h-[245px]"
          />
        ))}
      </div>
      <div className="mobile:hidden">
        <ButtonPaging
          className="!mt-[32px] mobile:!mt-[24px]"
          page={page}
          next={() => handlePageChange(page + 1)}
          prev={() => handlePageChange(page - 1)}
          total={total}
        />
      </div>
    </>
  );
};

export default AgendaCardGrid;
